import React, { useCallback, useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { KeyboardShortcuts } from "@/components/layout/KeyboardShortcuts";

import { AppShell } from "@/components/layout/AppShell";
import { QuickActionDialogs } from "@/components/layout/QuickActionDialogs";
import { FloatingChat } from "@/components/FloatingChat";
import { useSession } from "@/hooks/use-session";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Loader2 } from "lucide-react";

// Core pages - direct import (only auth + shell)
import LoginPage from "@/pages/LoginPage";
import PublicSigningPage from "@/pages/PublicSigningPage";

// Lazy-loaded pages (loaded on demand)
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const DashboardPage = lazy(() => import("@/pages/DashboardPageNew"));
const ClientsPage = lazy(() => import("@/pages/ClientsPage"));
const ClientDetailsPage = lazy(() => import("@/pages/ClientDetailsPage"));
const TasksPage = lazy(() => import("@/pages/TasksPageNew"));

// Lazy-loaded pages (loaded on demand)
const TaskDetailsPage = lazy(() => import("@/pages/TaskDetailsPage"));
const EmailPage = lazy(() => import("@/pages/EmailPage"));
const CalendarPage = lazy(() => import("@/pages/CalendarPageNew"));
const ReportsPage = lazy(() => import("@/pages/ReportsPageAdvanced"));
const InvoicesPage = lazy(() => import("@/pages/InvoicesPage"));
const InvoiceFormPage = lazy(() => import("@/pages/InvoiceFormPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPageNew"));
const AuditPage = lazy(() => import("@/pages/AuditPage"));
const SMSGatewayPage = lazy(() => import("@/pages/SMSGatewayPage"));
const AutomationPage = lazy(() => import("@/pages/AutomationPage"));
const SalesPipelinePage = lazy(() => import("@/pages/SalesPipelinePage"));
const ContactCenterPage = lazy(() => import("@/pages/ContactCenterPage"));
const UserPermissionsPage = lazy(() => import("@/pages/UserPermissionsPage"));
const MarketingPage = lazy(() => import("@/pages/MarketingPage"));
const MyDayPage = lazy(() => import("@/pages/MyDayPage"));
const SEOPage = lazy(() => import("@/pages/SEOPage"));
const DocumentsPage = lazy(() => import("@/pages/DocumentsPage"));
const SignaturesPage = lazy(() => import("@/pages/SignaturesPage"));
const AutentiImportPage = lazy(() => import("@/pages/AutentiImportPage"));
const AnalyticsDashboardPage = lazy(() => import("@/pages/AnalyticsDashboardPage"));
const JPKConverterPage = lazy(() => import("@/pages/JPKConverterPage"));
const SemrushPage = lazy(() => import("@/pages/SemrushPage"));
const TeamsPage = lazy(() => import("@/pages/TeamsPage"));
const TaskTemplatesPage = lazy(() => import("@/pages/TaskTemplatesPage"));
const MonthlyReportPage = lazy(() => import("@/pages/MonthlyReportPage"));
const ContractsPage = lazy(() => import("@/pages/ContractsPage"));
const AnnexesPage = lazy(() => import("@/pages/AnnexesPage"));
const ClientProfitabilityPage = lazy(() => import("@/pages/ClientProfitabilityPage"));
const EmailReportsPage = lazy(() => import("@/pages/EmailReportsPage"));
const SalaryCalculatorPage = lazy(() => import("@/pages/SalaryCalculatorPage"));
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";

// Register Service Worker for PWA
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => {});
  });
}

function Protected({ session, children }) {
  // Show loading state while session is being verified
  if (session.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A1C3A]"></div>
      </div>
    );
  }
  if (!session.user) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function AppInner() {
  const session = useSession();
  const [search, setSearch] = useState("");
  const [quickActionKey, setQuickActionKey] = useState("");
  const [chatUnreadCount, setChatUnreadCount] = useState(0);

  // Poll for chat unread count (separate from FloatingChat to show in nav)
  useEffect(() => {
    if (!session.user || !session.permissions?.contact_center?.includes("view")) {
      return;
    }

    const fetchUnread = async () => {
      try {
        const [channelsRes, convsRes] = await Promise.all([
          api.get("/chat/channels"),
          api.get("/chat/conversations")
        ]);
        const channelUnread = (channelsRes.data.channels || []).reduce((sum, c) => sum + (c.unreadCount || 0), 0);
        const dmUnread = (convsRes.data.conversations || []).reduce((sum, c) => sum + (c.unreadCount || 0), 0);
        setChatUnreadCount(channelUnread + dmUnread);
      } catch (e) {
        // Ignore fetch errors
      }
    };

    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, [session.user, session.permissions]);

  const onLogout = useCallback(() => {
    session.logout();
    toast.success("Wylogowano");
  }, [session]);

  const onQuickAction = useCallback(
    (key) => {
      setQuickActionKey(key);
    },
    []
  );

  const LazyFallback = <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  return (
    <HelmetProvider>
      <ErrorBoundary>
      <Toaster richColors />
      <BrowserRouter>
        <Suspense fallback={LazyFallback}>
        <Routes>
          <Route path="/login" element={<LoginPage session={session} />} />
          <Route path="/zapomniane-haslo" element={<ForgotPasswordPage />} />
          <Route path="/reset-haslo/:token" element={<ResetPasswordPage />} />
          <Route path="/dokumentacja-prawna" element={<LegalPage />} />
          
          {/* Public signing page - no authentication required */}
          <Route path="/podpis/:token" element={<PublicSigningPage />} />

          <Route
            path="/"
            element={
              <Protected session={session}>
                <AppShell
                  role={session.role}
                  permissions={session.permissions}
                  onLogout={onLogout}
                  onQuickAction={onQuickAction}
                  search={search}
                  setSearch={setSearch}
                  chatUnreadCount={chatUnreadCount}
                />
                <KeyboardShortcuts onQuickAction={onQuickAction} />
                <QuickActionDialogs 
                  openKey={quickActionKey} 
                  setOpenKey={setQuickActionKey} 
                  session={session} 
                />
              </Protected>
            }
          >
            <Route index element={<DashboardPage session={session} />} />
            <Route path="moj-dzien" element={<MyDayPage session={session} />} />
            <Route path="klienci" element={<ClientsPage globalSearch={search} session={session} />} />
            <Route path="klienci/:clientId" element={<ClientDetailsPage session={session} />} />
            <Route path="e-maile" element={<EmailPage />} />
            <Route path="zadania" element={<TasksPage session={session} />} />
            <Route path="zadania/:taskId" element={<TaskDetailsPage session={session} />} />
            <Route path="kalendarz" element={<CalendarPage session={session} />} />
            <Route path="raporty" element={<ReportsPage session={session} />} />
            <Route path="faktury" element={<InvoicesPage session={session} />} />
            <Route path="faktury/nowa" element={<InvoiceFormPage session={session} />} />
            <Route path="faktury/:invoiceId/edytuj" element={<InvoiceFormPage session={session} />} />
            <Route path="ustawienia" element={<SettingsPage session={session} />} />
            <Route path="audyt" element={<AuditPage session={session} />} />
            <Route path="sms" element={<SMSGatewayPage session={session} />} />
            <Route path="automatyzacja" element={<AutomationPage session={session} />} />
            <Route path="lejek" element={<SalesPipelinePage session={session} />} />
            <Route path="kontakt" element={<ContactCenterPage session={session} />} />
            <Route path="uprawnienia" element={<UserPermissionsPage session={session} />} />
            <Route path="marketing" element={<MarketingPage session={session} />} />
            <Route path="seo" element={<SEOPage />} />
            <Route path="analityka" element={<AnalyticsDashboardPage />} />
            <Route path="dokumenty" element={<DocumentsPage session={session} />} />
            <Route path="podpisy" element={<SignaturesPage session={session} />} />
            <Route path="integracje/autenti" element={<AutentiImportPage session={session} />} />
            <Route path="jpk" element={<JPKConverterPage />} />
            <Route path="semrush" element={<SemrushPage />} />
            <Route path="zespoly" element={<TeamsPage session={session} />} />
            <Route path="szablony-zadan" element={<TaskTemplatesPage session={session} />} />
            <Route path="raport-miesieczny" element={<MonthlyReportPage session={session} />} />
            <Route path="umowy" element={<ContractsPage session={session} />} />
            <Route path="aneksy" element={<AnnexesPage session={session} />} />
            <Route path="rentownosc" element={<ClientProfitabilityPage session={session} />} />
            <Route path="raporty-email" element={<EmailReportsPage session={session} />} />
            <Route path="kalkulator-wynagrodzen" element={<SalaryCalculatorPage />} />
          </Route>
        </Routes>
        </Suspense>
        
        {/* Floating Chat - visible on all pages when logged in */}
        {session.user && session.permissions?.contact_center?.includes("view") && (
          <FloatingChat session={session} />
        )}
        
        {/* PWA Install Prompt */}
        {session.user && <PWAInstallPrompt />}
      </BrowserRouter>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

function App() {
  return <AppInner />;
}

export default App;
